import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../utils/api';

// ── "The transfer didn't match — which is it?" (owner 2026-08-28) ───────────────────────────────
// An international wire routinely lands short (bank charges / FX) or over. This asks the ONE
// question that tells the two cases apart, and it always defaults to the safe answer:
//   ○ instalment  → leave the balance outstanding (exactly today's behaviour)
//   ○ bank fee    → attribute the gap so the document settles in full
// Never automatic, one receipt at a time. The server recomputes the amount and refuses gaps too
// large to be a fee (>10% of the outstanding balance) — see services/bankFeeGap.ts.

export interface FeeGapData {
  outstanding: number; received: number; gap: number; ratio: number;
  tier: 'none' | 'free' | 'reason' | 'blocked';
  parentKind: 'invoice' | 'quotation' | null; parentNo: string; feeAmount: number; feeReason: string;
}

const money = (n: number) => '฿' + Math.abs(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function FeeGapPrompt({ receiptId, gap, onChanged, compact }: {
  receiptId: string; gap: FeeGapData; onChanged?: () => void; compact?: boolean;
}) {
  const { t } = useTranslation();
  const [choice, setChoice] = useState<'partial' | 'fee' | 'wht'>('partial');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState<'fee' | 'cleared' | 'wht' | null>(null);

  const short = gap.gap > 0;                 // banks took it
  const pct = (gap.ratio * 100).toFixed(1);
  const settle = (gap as any).whtMode === 'settle';   // WHT option for a client whose PND.3/53 we don't file

  // WHT split already applied → green bar + undo (owner/Calem 2026-09-02).
  if ((gap as any).whtApplied && done !== 'cleared') {
    const undo = async () => {
      setBusy(true); setErr('');
      try { await api.receiptWhtFull(receiptId, true); setDone('cleared'); onChanged?.(); }
      catch (e: any) { setErr(e.message); } finally { setBusy(false); }
    };
    return (
      <div style={{ background: '#E3EFE8', border: '1px solid #9BD0B5', borderRadius: 10, padding: '10px 13px', fontSize: 12.5, color: '#276749', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <i className="ti ti-circle-check" style={{ fontSize: 15 }} aria-hidden />
        <span style={{ fontWeight: 700 }}>{t('fg_wht_applied', { amt: money((gap as any).whtAmount || 0) })}</span>
        <button onClick={undo} disabled={busy} style={{ marginLeft: 'auto', padding: '4px 11px', borderRadius: 8, border: '1px solid #9BD0B5', background: '#fff', color: '#276749', fontSize: 11.5, fontWeight: 700, cursor: busy ? 'default' : 'pointer' }}>{t('fg_undo')}</button>
        {err && <div style={{ width: '100%', color: '#B42318', fontSize: 12 }}>{err}</div>}
      </div>
    );
  }

  // Already attributed → show it, with a way back.
  if (gap.feeAmount) {
    const clear = async () => {
      setBusy(true); setErr('');
      try { await api.setReceiptFee(receiptId, { clear: true }); setDone('cleared'); onChanged?.(); }
      catch (e: any) { setErr(e.message); } finally { setBusy(false); }
    };
    if (done === 'cleared') return null;
    return (
      <div style={{ background: '#E3EFE8', border: '1px solid #9BD0B5', borderRadius: 10, padding: '10px 13px', fontSize: 12.5, color: '#276749', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <i className="ti ti-circle-check" style={{ fontSize: 15 }} aria-hidden />
        <span style={{ fontWeight: 700 }}>
          {gap.feeAmount > 0 ? t('fg_applied', { amt: money(gap.feeAmount) }) : t('fg_applied_over', { amt: money(gap.feeAmount) })}
        </span>
        {gap.feeReason && <span style={{ color: '#4B5563' }}>· {gap.feeReason}</span>}
        <button onClick={clear} disabled={busy} style={{ marginLeft: 'auto', padding: '4px 11px', borderRadius: 8, border: '1px solid #9BD0B5', background: '#fff', color: '#276749', fontSize: 11.5, fontWeight: 700, cursor: busy ? 'default' : 'pointer' }}>{t('fg_undo')}</button>
        {err && <div style={{ width: '100%', color: '#B42318', fontSize: 12 }}>{err}</div>}
      </div>
    );
  }

  if (gap.tier === 'none' || done === 'fee' || done === 'wht' || done === 'cleared') return null;

  const blocked = gap.tier === 'blocked';
  const apply = async () => {
    setBusy(true); setErr('');
    try { await api.setReceiptFee(receiptId, { reason }); setDone('fee'); onChanged?.(); }
    catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <div style={{ background: '#FFFBEB', border: '1px solid #F1C232', borderRadius: 10, padding: compact ? '10px 13px' : '13px 15px', fontSize: 12.5, color: '#78350F' }}>
      <div style={{ fontWeight: 800, marginBottom: 4 }}>
        <i className={`ti ti-${short ? 'arrow-down-right' : 'arrow-up-right'}`} style={{ marginRight: 6 }} aria-hidden />
        {short ? t('fg_short_title', { amt: money(gap.gap), pct }) : t('fg_over_title', { amt: money(gap.gap), pct })}
      </div>
      <div style={{ color: '#92400E', marginBottom: 9 }}>
        {t('fg_line', { doc: gap.parentNo || '—', out: money(gap.outstanding), got: money(gap.received) })}
      </div>
      {blocked ? (
        <div style={{ color: '#B42318', fontWeight: 600 }}><i className="ti ti-lock" style={{ marginRight: 5 }} />{t('fg_blocked', { pct })}</div>
      ) : (
        <>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 7, cursor: 'pointer', marginBottom: 5 }}>
            <input type="radio" checked={choice === 'partial'} onChange={() => setChoice('partial')} style={{ marginTop: 2, cursor: 'pointer' }} />
            <span><b>{t('fg_opt_partial')}</b> — {t('fg_opt_partial_hint')}</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 7, cursor: 'pointer' }}>
            <input type="radio" checked={choice === 'fee'} onChange={() => setChoice('fee')} style={{ marginTop: 2, cursor: 'pointer' }} />
            <span><b>{short ? t('fg_opt_fee') : t('fg_opt_over')}</b> — {t('fg_opt_fee_hint', { doc: gap.parentNo || '—' })}</span>
          </label>
          {/* Owner/Calem 2026-09-02: the client paid the GROSS — they did not withhold. The extra
              is this receipt's WHT (within ฿1 of rounding), so offer the truthful third reading.
              Our accounting client: the net settles the document, the WHT is tax money we hold and
              file under their PND.3/53. Any other Thai company/partnership ('settle', Calem
              2026-09-29): the document settles in full and the WHT stays on the receipt. */}
          {(gap as any).whtOption && (
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 7, cursor: 'pointer', marginTop: 5 }}>
              <input type="radio" checked={choice === 'wht'} onChange={() => setChoice('wht')} style={{ marginTop: 2, cursor: 'pointer' }} />
              <span><b>{t('fg_opt_wht', { amt: money((gap as any).whtAmount || 0) })}</b> — {settle ? t('fg_opt_wht_settle_hint', { doc: gap.parentNo || '—' }) : t('fg_opt_wht_hint', { doc: gap.parentNo || '—' })}</span>
            </label>
          )}
          {choice === 'wht' && (
            <div style={{ marginTop: 9 }}>
              <button onClick={async () => {
                setBusy(true); setErr('');
                try { await api.receiptWhtFull(receiptId); setDone('wht'); onChanged?.(); }
                catch (e: any) { setErr(e.message); } finally { setBusy(false); }
              }} disabled={busy}
                style={{ padding: '7px 16px', borderRadius: 8, border: 'none', background: '#0E7A64', color: '#fff', fontSize: 12.5, fontWeight: 800, cursor: busy ? 'default' : 'pointer' }}>
                {busy ? '…' : settle
                  ? t('fg_wht_settle_apply', { amt: money((gap as any).whtAmount || 0), doc: gap.parentNo || '—' })
                  : t('fg_wht_apply', { amt: money((gap as any).whtAmount || 0), doc: gap.parentNo || '—' })}
              </button>
            </div>
          )}
          {choice === 'fee' && (
            <div style={{ marginTop: 9 }}>
              {gap.tier === 'reason' && (
                <input value={reason} onChange={e => setReason(e.target.value)} placeholder={t('fg_reason_ph')}
                  style={{ width: '100%', padding: '7px 10px', fontSize: 12.5, borderRadius: 8, border: '1px solid #F1C232', background: '#fff', color: '#27303B', outline: 'none', boxSizing: 'border-box', marginBottom: 7 }} />
              )}
              <button onClick={apply} disabled={busy || (gap.tier === 'reason' && !reason.trim())}
                style={{ padding: '7px 16px', borderRadius: 8, border: 'none', background: '#B45309', color: '#fff', fontSize: 12.5, fontWeight: 800, cursor: busy ? 'default' : 'pointer', opacity: (gap.tier === 'reason' && !reason.trim()) ? 0.55 : 1 }}>
                {busy ? '…' : t('fg_apply', { amt: money(gap.gap) })}
              </button>
            </div>
          )}
        </>
      )}
      {err && <div style={{ color: '#B42318', marginTop: 8, fontWeight: 600 }}>{err}</div>}
    </div>
  );
}
